/**
 * Types for the NYCBS SFTP-push config document at the fixed Firestore path
 * `api_config/pharmacy/nycbs_sftp_push/latest`. The document drives how the
 * pharmacy SFTP push file is built (`csv`) and how resubmits are filtered and
 * re-statused (`resubmit`), plus CMM-status validation (`validation`).
 *
 * Clause shape note: `resubmit.fetch_filter` and `resubmit.status_update_conditions`
 * clauses use `column` + `op`, while `csv.response_status_mapping.special_cases`
 * clauses use `field` (equality implied, no `op`). The clause type below is
 * permissive so both round-trip without loss.
 */

/** A single leaf condition or a nested clause group. */
export interface SftpClause {
  /** Column-style field name (fetch_filter / status_update_conditions). */
  column?: string;
  /** Field-style field name (response_status_mapping special_cases). */
  field?: string;
  /** Comparison operator for column-style clauses (e.g. `=`, `!=`). */
  op?: string;
  /** Compared value; a list for `IN` / `NOT IN` clauses. */
  value?: string | string[];
  /** Present when this clause is itself a nested group. */
  clauses?: SftpClause[];
  /** Conjunction for a nested-group clause. */
  operator?: "AND" | "OR";
}

/** Flat `{operator, clauses}` group (resubmit fetch filter). */
export interface SftpClauseGroup {
  operator: "AND" | "OR";
  clauses: SftpClause[];
}

/** One response-status group: many statuses collapse to a single output code. */
export interface SftpStatusGroup {
  code: string;
  statuses: string[];
  _comment?: string;
}

/** A special-case response-status rule combining clauses to an output code. */
export interface SftpSpecialCase {
  operator: "AND" | "OR";
  code: string;
  clauses: SftpClause[];
  _comment?: string;
}

export interface SftpResponseStatusMapping {
  status_groups: SftpStatusGroup[];
  special_cases: SftpSpecialCase[];
}

/** One resubmit status-update rule producing a `result` when its clauses match. */
export interface SftpStatusUpdateCondition {
  operator: "AND" | "OR";
  result: string;
  clauses: SftpClause[];
  _comment?: string;
}

export interface NycbsSftpPushDoc {
  id?: string;
  csv: {
    columns: string[];
    column_header_mapping: Record<string, string>;
    response_status_mapping: SftpResponseStatusMapping;
  };
  validation: { valid_cmm_statuses: string[] };
  resubmit: {
    fetch_filter: SftpClauseGroup;
    status_update_conditions: SftpStatusUpdateCondition[];
    filename_prefix: string;
  };
}

/** Result options for a resubmit status-update rule. */
export const SFTP_RESULT_OPTIONS = [
  { value: "success", label: "Success" },
  { value: "resubmit", label: "Resubmit" },
];

/** Second-STP-status options shown in the response-status mapping rows. */
export const SECOND_STP_STATUS_OPTIONS = [
  { value: "", label: "—" },
  { value: "sent_to_plan", label: "Sent to Plan" },
  { value: "waiting_for_clinical", label: "Waiting for Clinical" },
];

/** Response-status options shown as tags in the response-status mapping rows. */
export const RESPONSE_STATUS_OPTIONS = [
  "Approved",
  "Auth Not Required",
  "Approval on File",
  "Denied",
  "Drug Not Covered",
  "Denial on File",
  "Pending",
  "NA Outcome",
];

/**
 * Builds an empty document used when the Firestore doc is missing.
 *
 * @return NycbsSftpPushDoc: A document with empty sections
 */
export function createEmptyNycbsSftpPushDoc(): NycbsSftpPushDoc {
  return {
    csv: {
      columns: [],
      column_header_mapping: {},
      response_status_mapping: { status_groups: [], special_cases: [] },
    },
    validation: { valid_cmm_statuses: [] },
    resubmit: {
      fetch_filter: { operator: "AND", clauses: [] },
      status_update_conditions: [],
      filename_prefix: "",
    },
  };
}
